import React, { useState, useEffect } from 'react';
import ReactMarkdown from 'markdown-to-jsx';

interface AnimatedMarkdownProps {
	content: string;
	typingSpeed: number;
	onComplete?: () => void;
	onStart?: () => void;
}

interface StreamingMarkdownProps {
	content: string;
	isStreaming: boolean;
	onStreamingStatusChange?: (status: 'streaming' | 'complete') => void;
}

const CustomImage = ({ src, alt }: { src: string; alt?: string }) => (
	<div
		style={{
			backgroundImage: `url(${src})`,
			backgroundPosition: 'center center',
			backgroundSize: 'contain',
			backgroundRepeat: 'no-repeat',
			display: 'block',
			width: 80,
			height: 80,
			margin: '0.5rem 0',
			border: '1px solid #ccc',
			borderRadius: '4px',
		}}
		role="img"
		aria-label={alt || 'image'}
	/>
);

const CustomIframe = ({ src, title }: { src: string; title: string }) => (
	<div style={{ margin: '10px 0', borderRadius: '2px', width: '100%', paddingBottom: '52%', position: 'relative' }}>
		<iframe 
			style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', border: 0 }}
			src={src}
			title={title}
		/>
	</div>
);

const CustomLink = ({ href, children }: { href: string; children: React.ReactNode }) => (
	<a 
		href={href} 
		target="_blank" 
		rel="noopener noreferrer" 
		style={{ wordBreak: 'break-word' }}
	>
		{children}
	</a>
);

const options = {
	overrides: {
		img: CustomImage,
		iframe: CustomIframe,
		a: CustomLink,
	},
};

const AnimatedMarkdown: React.FC<AnimatedMarkdownProps> = ({
	content,
	typingSpeed,
	onStart,
	onComplete,
}) => {
	const [displayedContent, setDisplayedContent] = useState('');
	const [currentIndex, setCurrentIndex] = useState(0);

	useEffect(() => {
		setDisplayedContent('');
		setCurrentIndex(0);
	}, [content]);

	useEffect(() => {
		if (currentIndex === 0) {
			onStart?.();
		}

		if (currentIndex < content.length) {
			const timer = setTimeout(() => {
				setDisplayedContent(content.slice(0, currentIndex + 1));
				setCurrentIndex((prev) => prev + 1);
			}, typingSpeed / 10);

			return () => clearTimeout(timer);
		} else if (currentIndex === content.length) {
			onComplete?.();
		}
	}, [currentIndex, content, typingSpeed, onStart, onComplete]);

	return (
		<span style={{ whiteSpace: 'pre-line' }}>
			<ReactMarkdown options={options}>{displayedContent}</ReactMarkdown>
		</span>
	);
};

export const StreamingMarkdown: React.FC<StreamingMarkdownProps> = ({
	content,
	isStreaming,
	onStreamingStatusChange
}) => {
	useEffect(() => {
		if (isStreaming) {
			onStreamingStatusChange?.('streaming');
		} else {
			onStreamingStatusChange?.('complete');
		}
	}, [isStreaming, onStreamingStatusChange]);

	return (
		<span style={{ whiteSpace: 'pre-line' }}>
			<ReactMarkdown options={options}>{content}</ReactMarkdown>
		</span>
	);
};

export const SimpleMarkdown: React.FC<{content: string}> = ({
	content
}) => {
	return (
		<ReactMarkdown options={options}>{content}</ReactMarkdown>
	);
};

export default AnimatedMarkdown;
